import React, { useState, useEffect } from 'react';
import { doc, updateDoc, deleteDoc, Timestamp, arrayUnion, getDoc } from 'firebase/firestore';
import { db, auth } from './firebase';
import RentalCalendar from './RentalCalendar';
import './Marketplace.css';

// Item categories
const CATEGORIES = [
  { id: 'dresses', label: 'Dresses', icon: '👗' },
  { id: 'shoes', label: 'Shoes', icon: '👠' },
  { id: 'costumes', label: 'Costumes', icon: '🎭' },
  { id: 'accessories', label: 'Accessories', icon: '💎' },
  { id: 'practice-wear', label: 'Practice Wear', icon: '🩱' },
  { id: 'jewelry', label: 'Jewelry', icon: '📿' },
  { id: 'other', label: 'Other', icon: '📦' }
];

const CONDITIONS = {
  'new': 'New with tags',
  'like-new': 'Like New',
  'excellent': 'Excellent',
  'good': 'Good',
  'fair': 'Fair'
};

export default function MarketplaceItemDetail({ item, onClose, isOwner, isAdmin, onUpdate }) {
  const [activeImage, setActiveImage] = useState(0);
  const [showContact, setShowContact] = useState(false);
  const [rentalDates, setRentalDates] = useState({ start: null, end: null });
  const [bookingLoading, setBookingLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  const formatPrice = (price) => {
    if (!price) return 'N/A';
    return `€${Number(price).toLocaleString()}`;
  };

  const getCategoryLabel = (categoryId) => {
    const cat = CATEGORIES.find(c => c.id === categoryId);
    return cat ? `${cat.icon} ${cat.label}` : categoryId;
  };

  const getSellerInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  const calculateRentalTotal = () => {
    if (!rentalDates.start || !rentalDates.end || !item.rentPricePerDay) return 0;
    
    const start = new Date(rentalDates.start);
    const end = new Date(rentalDates.end);
    const days = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
    
    return days * item.rentPricePerDay;
  };

  const getRentalDays = () => {
    if (!rentalDates.start || !rentalDates.end) return 0;
    
    const start = new Date(rentalDates.start);
    const end = new Date(rentalDates.end);
    return Math.ceil((end - start) / (1000 * 60 * 60 * 24)) + 1;
  };

  const handleRentalDateSelect = (dates) => {
    setRentalDates(dates);
  };

  const handleBookRental = async () => {
    if (!rentalDates.start || !rentalDates.end) {
      setMessage('Please select rental dates');
      return;
    }

    const days = getRentalDays();
    if (days < (item.minRentalDays || 1)) {
      setMessage(`Minimum rental period is ${item.minRentalDays || 1} days`);
      return;
    }
    if (item.maxRentalDays && days > item.maxRentalDays) {
      setMessage(`Maximum rental period is ${item.maxRentalDays} days`);
      return;
    }

    setBookingLoading(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Please sign in to book');

      const booking = {
        startDate: rentalDates.start,
        endDate: rentalDates.end,
        renterId: user.uid,
        renterEmail: user.email,
        renterName: user.displayName || 'Anonymous',
        totalPrice: calculateRentalTotal(),
        status: 'pending', // pending, confirmed, completed, cancelled
        createdAt: Timestamp.now()
      };

      await updateDoc(doc(db, 'marketplaceItems', item.id), {
        rentalBookings: arrayUnion(booking),
        updatedAt: Timestamp.now()
      });

      setMessage('✅ Booking request sent! The seller will contact you to confirm.');
      setRentalDates({ start: null, end: null });
      
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error booking rental:', error);
      setMessage('❌ Failed to book. Please try again.');
    } finally {
      setBookingLoading(false);
    }
  };

  const handleMarkAsSold = async () => {
    if (!window.confirm('Mark this item as sold?')) return;
    
    setActionLoading(true);
    try {
      await updateDoc(doc(db, 'marketplaceItems', item.id), {
        status: 'sold',
        soldAt: Timestamp.now(),
        updatedAt: Timestamp.now()
      });
      setMessage('✅ Item marked as sold');
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error marking as sold:', error);
      setMessage('❌ Failed to update. Please try again.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteListing = async () => {
    if (!window.confirm('Are you sure you want to delete this listing? This cannot be undone.')) return;
    
    setActionLoading(true);
    try {
      await deleteDoc(doc(db, 'marketplaceItems', item.id));
      if (onUpdate) onUpdate();
      onClose();
    } catch (error) {
      console.error('Error deleting listing:', error);
      setMessage('❌ Failed to delete. Please try again.');
      setActionLoading(false);
    }
  };

  // Get booked dates for the calendar
  const getBookedDates = () => {
    if (!item.rentalBookings || item.rentalBookings.length === 0) return [];
    
    const bookedDates = [];
    item.rentalBookings
      .filter(b => b.status !== 'cancelled')
      .forEach(booking => {
        const start = new Date(booking.startDate);
        const end = new Date(booking.endDate);
        
        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
          bookedDates.push(new Date(d).toISOString().split('T')[0]);
        }
      });
    
    return bookedDates;
  };

  return (
    <div className="item-detail">
      {message && (
        <div className={`marketplace-alert ${message.startsWith('✅') ? 'success' : message.startsWith('❌') ? 'error' : 'info'}`}>
          <span className="alert-icon">{message.startsWith('✅') ? '✅' : message.startsWith('❌') ? '⚠️' : 'ℹ️'}</span>
          <div className="alert-content">
            <p>{message.replace(/^[✅❌ℹ️]\s*/, '')}</p>
          </div>
        </div>
      )}

      <div className="item-detail-grid">
        {/* Image Gallery */}
        <div className="item-gallery">
          {item.images && item.images.length > 0 ? (
            <>
              <img 
                src={item.images[activeImage]} 
                alt={item.title}
                className="main-image"
              />
              {item.images.length > 1 && (
                <div className="thumbnail-row">
                  {item.images.map((img, index) => (
                    <img
                      key={index}
                      src={img}
                      alt={`${item.title} ${index + 1}`}
                      className={`thumbnail ${activeImage === index ? 'active' : ''}`}
                      onClick={() => setActiveImage(index)}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="main-image" style={{ 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '4rem'
            }}>
              {CATEGORIES.find(c => c.id === item.category)?.icon || '📦'}
            </div>
          )}
        </div>

        {/* Item Info */}
        <div className="item-detail-info">
          <h1>{item.title}</h1>
          
          <div className="item-detail-price">
            {(item.listingType === 'sale' || item.listingType === 'both') && (
              <span className="detail-price-sale">{formatPrice(item.salePrice)}</span>
            )}
            {(item.listingType === 'rent' || item.listingType === 'both') && (
              <span className="detail-price-rent">
                {formatPrice(item.rentPricePerDay)}/day
                {item.deposit && ` + ${formatPrice(item.deposit)} deposit`}
              </span>
            )}
          </div>

          <div className="item-detail-meta">
            <div className="meta-item">
              <span className="meta-label">Category</span>
              <span className="meta-value">{getCategoryLabel(item.category)}</span>
            </div>
            <div className="meta-item">
              <span className="meta-label">Condition</span>
              <span className="meta-value">{CONDITIONS[item.condition] || item.condition}</span>
            </div>
            {item.size && (
              <div className="meta-item">
                <span className="meta-label">Size</span>
                <span className="meta-value">{item.size}</span>
              </div>
            )}
            {item.color && (
              <div className="meta-item">
                <span className="meta-label">Color</span>
                <span className="meta-value">{item.color}</span>
              </div>
            )}
            {item.brand && (
              <div className="meta-item">
                <span className="meta-label">Brand</span>
                <span className="meta-value">{item.brand}</span>
              </div>
            )}
            {item.danceStyle && (
              <div className="meta-item">
                <span className="meta-label">Dance Style</span>
                <span className="meta-value">{item.danceStyle}</span>
              </div>
            )}
            {item.location && (
              <div className="meta-item">
                <span className="meta-label">Location</span>
                <span className="meta-value">📍 {item.location}</span>
              </div>
            )}
          </div>

          <div className="item-detail-description">
            <p>{item.description}</p>
            {item.additionalInfo && (
              <p style={{ marginTop: '1rem', fontStyle: 'italic', opacity: 0.8 }}>
                {item.additionalInfo}
              </p>
            )}
          </div>

          {/* Seller Info */}
          <div className="seller-section">
            <div className="seller-avatar-large">
              {getSellerInitials(item.sellerName)}
            </div>
            <div className="seller-info">
              <h3>
                {item.sellerName}
                {item.isVerifiedSeller && (
                  <span style={{ 
                    marginLeft: '0.5rem', 
                    fontSize: '0.8rem',
                    color: '#69f0ae'
                  }}>✓ Verified Dancer</span>
                )}
              </h3>
              <p>Listed {item.createdAt?.toDate?.()?.toLocaleDateString() || 'Recently'}</p>
            </div>
          </div>

          {/* Rental Calendar (if rental available) */}
          {(item.listingType === 'rent' || item.listingType === 'both') && !isOwner && item.status === 'approved' && (
            <RentalCalendar
              bookedDates={getBookedDates()}
              onDateSelect={handleRentalDateSelect}
              selectedDates={rentalDates}
              minDays={item.minRentalDays || 1}
              maxDays={item.maxRentalDays || 30}
              pricePerDay={item.rentPricePerDay}
            />
          )}

          {/* Contact / Action Buttons */}
          {!isOwner && item.status === 'approved' && (
            <div className="contact-actions">
              {showContact ? (
                <div style={{ width: '100%' }}>
                  <div className="marketplace-alert info" style={{ marginBottom: '1rem' }}>
                    <span className="alert-icon">📧</span>
                    <div className="alert-content">
                      <h4>Contact Seller</h4>
                      <p>Email: {item.sellerEmail}</p>
                      {item.phone && <p>Phone: {item.phone}</p>}
                    </div>
                  </div>
                </div>
              ) : (
                <>
                  <button className="contact-btn primary" onClick={() => setShowContact(true)}>
                    <span>💬</span>
                    <span>Contact Seller</span>
                  </button>
                  
                  {(item.listingType === 'rent' || item.listingType === 'both') && rentalDates.start && rentalDates.end && (
                    <button 
                      className="contact-btn primary" 
                      onClick={handleBookRental}
                      disabled={bookingLoading}
                    >
                      <span>📅</span>
                      <span>
                        {bookingLoading 
                          ? 'Booking...' 
                          : `Book Rental (${formatPrice(calculateRentalTotal())})`}
                      </span>
                    </button>
                  )}
                </>
              )}
            </div>
          )}

          {/* Owner Actions */}
          {(isOwner || isAdmin) && (
            <div className="contact-actions" style={{ marginTop: '1.5rem' }}>
              {item.status !== 'sold' && (
                <button 
                  className="contact-btn secondary" 
                  onClick={handleMarkAsSold}
                  disabled={actionLoading}
                >
                  <span>✅</span>
                  <span>Mark as Sold</span>
                </button>
              )}
              
              <button 
                className="contact-btn secondary" 
                onClick={handleDeleteListing}
                disabled={actionLoading}
                style={{ background: 'rgba(233, 69, 96, 0.2)' }}
              >
                <span>🗑️</span>
                <span>Delete Listing</span>
              </button>
            </div>
          )}

          {/* Status Badge for owners */}
          {isOwner && (
            <div style={{ marginTop: '1.5rem' }}>
              <span className={`listing-status-badge ${item.status}`} style={{ position: 'relative' }}>
                {item.status === 'pending' && '⏳ Pending Review'}
                {item.status === 'approved' && '✅ Live'}
                {item.status === 'rejected' && '❌ Rejected'}
                {item.status === 'sold' && '💰 Sold'}
                {item.status === 'rented' && '📅 Currently Rented'}
              </span>
            </div>
          )}

          {/* Rental Bookings (for owners) */}
          {isOwner && item.rentalBookings && item.rentalBookings.length > 0 && (
            <div style={{ marginTop: '2rem' }}>
              <h3 style={{ color: 'white', marginBottom: '1rem' }}>📅 Rental Bookings</h3>
              {item.rentalBookings.map((booking, index) => (
                <div 
                  key={index}
                  style={{
                    background: 'rgba(255, 255, 255, 0.05)',
                    borderRadius: '10px',
                    padding: '1rem',
                    marginBottom: '0.75rem'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <strong style={{ color: 'white' }}>{booking.renterName}</strong>
                      <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem', margin: '0.25rem 0' }}>
                        {booking.startDate} → {booking.endDate}
                      </p>
                      <p style={{ color: '#ffd93d', fontWeight: '600' }}>
                        {formatPrice(booking.totalPrice)}
                      </p>
                    </div>
                    <span className={`listing-status-badge ${booking.status}`} style={{ position: 'relative' }}>
                      {booking.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
